import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@marekl/adminer";
import { ForceRefreshOverlay } from "./components/ForceRefreshOverlay";
import { useForcedRefresh } from "./lib/useForcedRefresh";
import { RequireAuth } from "./auth/RequireAuth";
import { RequireGuest } from "./auth/RequireGuest";
import { AppLayout } from "./shell/AppLayout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DailyOverviewPage } from "./pages/DailyOverviewPage";
import { UsersPage } from "./pages/UsersPage";
import { ProfilePage } from "./pages/ProfilePage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { ConfigPage } from "./pages/ConfigPage";
import { BarcodeBankPage } from "./pages/BarcodeBankPage";
import { CounterSalePage } from "./pages/CounterSalePage";
import { VouchersPage } from "./pages/VouchersPage";
import { VoucherDetailPage } from "./pages/VoucherDetailPage";
import { CertificatesPage } from "./pages/CertificatesPage";
import { EshopSyncPage } from "./pages/EshopSyncPage";
import { EmailProblemsPage } from "./pages/EmailProblemsPage";
import { ReportsPage } from "./pages/ReportsPage";

/** Redirects the old /certificates/redeem route to /certificates, keeping any ?code=… deep-link. */
function CertificatesRedirect() {
  const location = useLocation();
  return <Navigate to={`/certificates${location.search}`} replace />;
}

/** Redirects the old /vouchers/search route to /vouchers, preserving any filter params. */
function VouchersSearchRedirect() {
  const location = useLocation();
  return <Navigate to={`/vouchers${location.search}`} replace />;
}

export default function App() {
  // Mounted at App level (not AppLayout) so the forced-refresh overlay also
  // covers the login page.
  const refreshNeeded = useForcedRefresh();

  return (
    <AuthProvider>
      {refreshNeeded && <ForceRefreshOverlay />}
      <Routes>
        <Route
          path="/login"
          element={
            <RequireGuest>
              <LoginPage />
            </RequireGuest>
          }
        />
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route
            path="/daily-overview"
            element={
              <RequireAuth roles={["reception", "manager", "superadmin"]}>
                <DailyOverviewPage />
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth roles={["superadmin"]}>
                <UsersPage />
              </RequireAuth>
            }
          />
          <Route path="/profile" element={<ProfilePage />} />
          <Route
            path="/audit-log"
            element={
              <RequireAuth roles={["manager", "superadmin"]}>
                <AuditLogPage />
              </RequireAuth>
            }
          />
          <Route
            path="/config"
            element={
              <RequireAuth roles={["superadmin"]}>
                <ConfigPage />
              </RequireAuth>
            }
          />
          <Route
            path="/barcode-bank"
            element={
              <RequireAuth roles={["superadmin"]}>
                <BarcodeBankPage />
              </RequireAuth>
            }
          />
          <Route
            path="/vouchers"
            element={
              <RequireAuth roles={["reception", "manager", "superadmin"]}>
                <VouchersPage />
              </RequireAuth>
            }
          />
          {/* Back-compat: /vouchers/search was the old reception-only search route. */}
          <Route path="/vouchers/search" element={<VouchersSearchRedirect />} />
          <Route
            path="/vouchers/sale"
            element={
              <RequireAuth roles={["reception", "manager", "superadmin"]}>
                <CounterSalePage />
              </RequireAuth>
            }
          />
          <Route
            path="/vouchers/:id"
            element={
              <RequireAuth roles={["reception", "manager", "superadmin"]}>
                <VoucherDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/certificates"
            element={
              <RequireAuth roles={["reception", "manager", "superadmin"]}>
                <CertificatesPage />
              </RequireAuth>
            }
          />
          {/* Back-compat: the two certificate screens were unified into one. */}
          <Route path="/certificates/redeem" element={<CertificatesRedirect />} />
          <Route path="/certificates/admin" element={<Navigate to="/certificates" replace />} />
          <Route
            path="/email-problems"
            element={
              <RequireAuth roles={["manager", "superadmin"]}>
                <EmailProblemsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/eshop-sync"
            element={
              <RequireAuth roles={["manager", "superadmin"]}>
                <EshopSyncPage />
              </RequireAuth>
            }
          />
          <Route
            path="/reports"
            element={
              <RequireAuth roles={["manager", "superadmin"]}>
                <ReportsPage />
              </RequireAuth>
            }
          />
        </Route>
        {/* Fallback — redirect unknown paths to dashboard (auth guard handles the rest) */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AuthProvider>
  );
}
